import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from "react-redux";
import { logout, setCurrency, setLanguage } from "shared/stores/authSlice";
import { useTranslation } from "react-i18next";
import LanguageCurrencyDropdown from "./Widgets/LanguageCurrencyDropdown";
import { fetchRegions } from "shared/stores/store";
import { ROUTES } from '../routeConstants';

const Layout = ({ children }) => {
    const { t, i18n } = useTranslation('common');
    const dispatch = useDispatch();
    const { isAuthenticated, user, language, currency } = useSelector(state => state.auth);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showLanguageCurrencyDropdown, setShowLanguageCurrencyDropdown] = useState(false);
    const regions = useSelector((state) => state.regions.list);

    const footerNavigation = {
        estates: [
            { name: t('footer.sections.propertyTypes.popularListings'), href: ROUTES.POPULAR_ESTATES },
            { name: t('footer.sections.propertyTypes.newDevelopments'), href: ROUTES.LISTINGS },
            { name: t('footer.sections.propertyTypes.beachfrontHomes'), href: ROUTES.BEACHFRONT_HOMES },
            { name: t('footer.sections.propertyTypes.golfProperties'), href: ROUTES.GOLF_ESTATES },
        ],
        customerService: [
            { name: t('navigation.contact'), href: ROUTES.CONTACT },
            { name: t('navigation.about'), href: ROUTES.ABOUT },
        ],
        social: [
            {
                name: 'Facebook',
                href: 'https://www.facebook.com/profile.php?id=61567352835879',
                icon: (props) => (
                    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                        <path
                            fillRule="evenodd"
                            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                            clipRule="evenodd"
                        />
                    </svg>
                ),
            },
            {
                name: 'Instagram',
                href: 'https://www.instagram.com/infinitygroupspain2024/',
                icon: (props) => (
                    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
                        <path
                            fillRule="evenodd"
                            d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                            clipRule="evenodd"
                        />
                    </svg>
                ),
            },
            {
                name: 'TikTok',
                href: 'https://www.tiktok.com/@infinity_new_builds',
                icon: (props) => (
                    <svg fill="currentColor" viewBox="0 0 512 512" id="icons" xmlns="http://www.w3.org/2000/svg" {...props}>
                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                        <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                            <path
                                d="M412.19,118.66a109.27,109.27,0,0,1-9.45-5.5,132.87,132.87,0,0,1-24.27-20.62c-18.1-20.71-24.86-41.72-27.35-56.43h.1C349.14,23.9,350,16,350.13,16H267.69V334.78c0,4.28,0,8.51-.18,12.69,0,.52-.05,1-.08,1.56,0,.23,0,.47-.05.71,0,.06,0,.12,0,.18a70,70,0,0,1-35.22,55.56,68.8,68.8,0,0,1-34.11,9c-38.41,0-69.54-31.32-69.54-70s31.13-70,69.54-70a68.9,68.9,0,0,1,21.41,3.39l.1-83.94a153.14,153.14,0,0,0-118,34.52,161.79,161.79,0,0,0-35.3,43.53c-3.48,6-16.61,30.11-18.2,69.24-1,22.21,5.67,45.22,8.85,54.73v.2c2,5.6,9.75,24.71,22.38,40.82A167.53,167.53,0,0,0,115,470.66v-.2l.2.2C155.11,497.78,199.36,496,199.36,496c7.66-.31,33.32,0,62.46-13.81,32.32-15.31,50.72-38.12,50.72-38.12a158.46,158.46,0,0,0,27.64-45.93c7.46-19.61,9.95-43.13,9.95-52.53V176.49c1,.6,14.32,9.41,14.32,9.41s19.19,12.3,49.13,20.31c21.48,5.7,50.42,6.9,50.42,6.9V131.27C453.86,132.37,433.27,129.17,412.19,118.66Z"></path>
                        </g>
                    </svg>
                ),
            },
        ],
    };

    // Ensure language and currency are set correctly on mount
    useEffect(() => {
        if (language) {
            i18n.changeLanguage(language);
        }
    }, [language, i18n]);

    const handleLogout = () => {
        dispatch(logout());
        setMobileMenuOpen(false);
    };

    useEffect(() => {
        dispatch(fetchRegions());
    }, [dispatch]);

    const headerNavigation = [
        { name: t('navigation.properties'), href: ROUTES.LISTINGS },
        { name: t('navigation.mapSearch'), href: `${ROUTES.LISTINGS}?map` },
        { name: t('navigation.regions'), href: ROUTES.REGIONS },
        { name: t('navigation.about'), href: ROUTES.ABOUT },
        { name: t('navigation.contact'), href: ROUTES.CONTACT },
    ];

    const authButtons = isAuthenticated ? (
        <>
            <Link
                onClick={() => setMobileMenuOpen(false)}
                to={ROUTES.DASHBOARD}
                className="border-sky-700 px-3 py-2 text-sm font-semibold text-sky-700"
            >
                {t('auth.myPages')}
            </Link>
            <button
                onClick={handleLogout}
                className="rounded-md bg-sky-700 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500"
            >
                {t('auth.logout')}
            </button>
        </>
    ) : (
        <>
            <Link
                onClick={() => setMobileMenuOpen(false)}
                to={ROUTES.LOGIN}
                className="border-sky-700 px-3 py-2 text-sm font-semibold text-sky-700"
            >
                {t('auth.login')}
            </Link>
            <Link
                onClick={() => setMobileMenuOpen(false)}
                to={ROUTES.REGISTER}
                className="rounded-md bg-sky-700 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500"
            >
                {t('auth.register')}
            </Link>
        </>
    );

    const handleLanguageChange = (code) => {
        dispatch(setLanguage(code));
        i18n.changeLanguage(code);
        setShowLanguageCurrencyDropdown(false);
    };

    const handleCurrencyChange = (code) => {
        dispatch(setCurrency(code));
        setShowLanguageCurrencyDropdown(false);
    };

    const toggleLanguageCurrencyDropdown = () => {
        setShowLanguageCurrencyDropdown(!showLanguageCurrencyDropdown);
    };

    const LanguageSwitcher = () => {
        const dispatch = useDispatch();
        const currentLanguage = useSelector((state) => state.auth.language);

        const languages = [
            { code: 'da', label: 'Dansk' },
            { code: 'en', label: 'English' },
            { code: 'es', label: 'Español' },
            { code: 'sv', label: 'Svenska' },
            { code: 'no', label: 'Norsk' }
        ];

        return (
            <div className="flex gap-6">
                {languages.map(({ code, label }) => (
                    <button
                        key={code}
                        onClick={() => handleLanguageChange(code)}
                        className={`rounded text-xs bg-gray-900 uppercase ${currentLanguage === code ? 'text-white' : 'text-gray-400'
                            }`}
                    >
                        {label}
                    </button>
                ))}
            </div>
        );
    };

    const CurrencySwitcher = () => {
        const dispatch = useDispatch();
        const currentCurrency = useSelector((state) => state.auth.currency);

        const currencies = [
            { code: 'EUR', label: 'EUR' },
            { code: 'DKK', label: 'DKK' },
            { code: 'SEK', label: 'SEK' },
            { code: 'NOK', label: 'NOK' },
            { code: 'USD', label: 'USD' },
            { code: 'GBP', label: 'GBP' }
        ];

        return (
            <div className="flex gap-6">
                {currencies.map(({ code, label }) => (
                    <button
                        key={code}
                        onClick={() => handleCurrencyChange(code)}
                        className={`rounded text-xs bg-gray-900 uppercase ${currentCurrency === code ? 'text-white' : 'text-gray-400'
                            }`}
                    >
                        {label}
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col overflow-x-hidden">
            {/* Header */}
            <header className="bg-white border-b border-gray-100 fixed md:h-[69px] z-50 w-full">
                <nav aria-label="Global"
                    className="mx-auto flex max-w-7xl items-center justify-between gap-x-6 p-4 lg:px-8">
                    <div className="flex pr-10">
                        <Link to="/" className="-m-1.5 text-sky-900 text-lg font-bold">
                            <span className="sr-only">Your Company</span>
                            <img
                                alt="Infinity New Builds"
                                src="/logo-inb.png"
                                className={`h-6 md:h-8 w-auto`}
                            />
                        </Link>
                    </div>
                    <div className="hidden lg:flex lg:gap-x-10 flex-1 justify-center text-center">
                        {headerNavigation.map((item) => (
                            <Link key={item.name} to={item.href}
                                className="text-sm text-gray-900 font-semibold leading-6">
                                {item.name}
                            </Link>
                        ))}
                    </div>
                    <div className="hidden md:flex items-center justify-end gap-x-3">
                        {authButtons}
                        <LanguageCurrencyDropdown />
                    </div>
                    <div className="flex lg:hidden items-center gap-x-3">
                        <div className="md:hidden">
                            <LanguageCurrencyDropdown isMobile={true} />
                        </div>
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(true)}
                            className="-m-2.5 inline-flex items-center justify-center rounded-md p-3 text-gray-700"
                        >
                            <span className="sr-only">Open main menu</span>
                            <Bars3Icon aria-hidden="true" className="h-7 w-7" />
                        </button>
                    </div>
                </nav>
                <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
                    <div className="fixed inset-0 z-10" />
                    <Dialog.Panel
                        className="top-[57px] fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                        <div className="mt-6 flow-root">
                            <div className="-my-6 divide-y divide-gray-500/10">
                                <div className="pb-6">
                                    {authButtons}
                                </div>
                                <div className="space-y-2 py-6">
                                    {headerNavigation.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            {item.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Dialog.Panel>
                </Dialog>
            </header>

            {/* Main content */}
            <main className="flex-grow" style={{ marginTop: '69px' }}>
                {children}
            </main>
            <footer aria-labelledby="footer-heading" className="bg-white">
                <h2 id="footer-heading" className="sr-only">
                    {t('footer.sections.footer')}
                </h2>
                <div className="mx-auto max-w-7xl px-6 pb-8 pt-16 sm:pt-24 lg:px-8 lg:pt-32">
                    <div className="xl:grid xl:grid-cols-2 xl:gap-8">
                        <div className="xl:max-w-xl">
                            <img
                                alt={t('footer.companyLogo')}
                                src="/logo-square-inb.png"
                                className="h-36"
                            />
                        </div>
                        <div className="mt-16 grid grid-cols-3 gap-8 xl:mt-0">
                            <div>
                                <h3 className="text-sm font-semibold leading-6 text-gray-900">{t('footer.sections.properties')}</h3>
                                <ul role="list" className="mt-6 space-y-4">
                                    {footerNavigation.estates.map((item) => (
                                        <li key={item.name}>
                                            <Link to={item.href}
                                                className="text-sm leading-6 text-gray-600 hover:text-gray-900">
                                                {item.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold leading-6 text-gray-900">{t('footer.sections.regions')}</h3>
                                <ul role="list" className="mt-6 space-y-4">
                                    {regions.map((region) => (
                                        <li key={region.id}>
                                            <Link to={`/region/${region.id}`}
                                                className="text-sm leading-6 text-gray-600 hover:text-gray-900">
                                                {region.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold leading-6 text-gray-900">{t('footer.sections.customerService')}</h3>
                                <ul role="list" className="mt-6 space-y-4">
                                    {footerNavigation.customerService.map((item) => (
                                        <li key={item.name}>
                                            <Link to={item.href}
                                                className="text-sm leading-6 text-gray-600 hover:text-gray-900">
                                                {item.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div
                        className="mt-16 border-t border-gray-900/10 pt-8 sm:mt-20 lg:mt-24 lg:flex lg:items-center lg:justify-between">
                        <div>
                            <h3 className="text-sm font-semibold leading-6 text-gray-900">{t('footer.newsletter.title')}</h3>
                            <p className="mt-2 text-sm leading-6 text-gray-600">
                                {t('footer.newsletter.description')}
                            </p>
                        </div>
                        <form className="mt-6 sm:flex sm:max-w-md lg:mt-0">
                            <label htmlFor="email-address" className="sr-only">
                                {t('footer.newsletter.emailPlaceholder')}
                            </label>
                            <input
                                id="email-address"
                                name="email-address"
                                type="email"
                                autoComplete="email"
                                required
                                className="w-full min-w-0 appearance-none rounded-md border-0 bg-white px-3 py-1.5 text-base text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:w-56 sm:text-sm sm:leading-6"
                                placeholder={t('footer.newsletter.emailPlaceholder')}
                            />
                            <div className="mt-4 sm:ml-4 sm:mt-0 sm:flex-shrink-0">
                                <button
                                    type="submit"
                                    className="flex w-full items-center justify-center rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                                >
                                    {t('footer.newsletter.subscribe')}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className="mt-8 border-t border-gray-900/10 pt-8 md:flex md:items-center md:justify-between">
                        <div className="flex space-x-6 md:order-2">
                            {footerNavigation.social.map((item) => (
                                <a key={item.name} href={item.href} className="text-gray-400 hover:text-gray-500">
                                    <span className="sr-only">{item.name}</span>
                                    <item.icon className="h-6 w-6" aria-hidden="true" />
                                </a>
                            ))}
                        </div>
                        <p className="mt-8 text-xs leading-5 text-gray-500 md:order-1 md:mt-0">
                            {t('footer.copyright', { year: new Date().getFullYear() })}
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Layout;